import React from 'react';
import { formatCurrency, formatDate, translateStatut } from '../../utils/formatters';
import { Eye, Truck, FileText, FileX2 } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

const Dashboard = ({
  nbCommandesAffichees,
  setNbCommandesAffichees,
  handleTabChange,
  setEditingCommande,
  setShowCommandeForm,
  genererBonLivraison,
  genererFacture,
  genererAvoir,
}) => {
  const { currentUser, clients, produits, commandes, settings } = useApp();
  // Calculer le CA du jour
  const caJour = commandes
    .filter(c => c.date_commande && new Date(c.date_commande).toDateString() === new Date().toDateString())
    .reduce((sum, c) => sum + c.total, 0);

  return (
    <div className="dashboard" data-testid="dashboard">
      <div className="section-header">
        <h2>Tableau de bord</h2>
        <div className="user-info">
          <span>Connecté en tant que: <strong>{currentUser?.username}</strong> ({currentUser?.role})</span>
        </div>
      </div>
      
      {/* Cartes de statistiques */}
      <div className="stats-grid">
        <div 
          className="stat-card stat-card-clickable" 
          onClick={() => handleTabChange('clients')} 
          title="Voir tous les clients"
          data-testid="stat-card-clients"
        >
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Clients</h3>
            <p className="stat-number">{clients.length}</p>
          </div>
        </div>
        
        <div 
          className="stat-card stat-card-clickable" 
          onClick={() => handleTabChange('produits')} 
          title="Voir tous les produits"
          data-testid="stat-card-produits"
        >
          <div className="stat-icon">🥕</div>
          <div className="stat-content">
            <h3>Produits</h3>
            <p className="stat-number">{produits.length}</p>
          </div>
        </div>
        
        <div 
          className="stat-card stat-card-clickable" 
          onClick={() => handleTabChange('commandes')} 
          title="Voir toutes les commandes"
          data-testid="stat-card-commandes"
        >
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>Commandes</h3>
            <p className="stat-number">{commandes.length}</p>
          </div>
        </div>
        
        <div 
          className="stat-card stat-card-clickable" 
          onClick={() => handleTabChange('statistiques')} 
          title="Voir les statistiques détaillées"
          data-testid="stat-card-ca"
        >
          <div className="stat-icon">💶</div>
          <div className="stat-content">
            <h3>CA du jour</h3>
            <p className="stat-number">{formatCurrency(caJour, settings?.devise)}</p>
          </div>
        </div>
      </div>
      
      {/* Commandes récentes */}
      <div className="recent-orders-section">
        <div className="section-header-mini">
          <h3>Commandes récentes</h3>
          <div className="orders-count">
            <label htmlFor="nb-commandes-select" style={{marginRight: '10px'}}>Afficher :</label>
            <select 
              id="nb-commandes-select"
              value={nbCommandesAffichees} 
              onChange={(e) => setNbCommandesAffichees(Number(e.target.value))}
              className="form-select-sm"
              style={{padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd'}}
              data-testid="nb-commandes-select"
            >
              <option value={5}>5 dernières</option>
              <option value={10}>10 dernières</option>
              <option value={15}>15 dernières</option>
            </select>
          </div>
        </div>
        
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Date</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commandes.slice(0, nbCommandesAffichees).map(commande => (
                <tr key={commande.id} data-testid={`commande-row-${commande.id}`}>
                  <td>{commande.nom_client}</td>
                  <td>{formatDate(commande.date_commande)}</td>
                  <td>{formatCurrency(commande.total, settings?.devise)}</td>
                  <td>
                    <span className={`status ${commande.statut}`}>
                      {translateStatut(commande.statut)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {/* Bouton "VOIR" pour brouillon et en_cours */}
                      {(commande.statut === 'brouillon' || commande.statut === 'en_cours') && (
                        <button 
                          onClick={() => {
                            handleTabChange('commandes');
                            setEditingCommande(commande);
                            setShowCommandeForm(true);
                          }}
                          className="btn-action btn-view"
                          title="Voir et modifier la commande"
                          data-testid={`btn-voir-${commande.id}`}
                        >
                          <Eye size={16} />
                        </button>
                      )}
                      
                      {/* Bouton "Bon" pour les autres statuts */}
                      {commande.statut !== 'brouillon' && commande.statut !== 'en_cours' && (
                        <button 
                          onClick={() => genererBonLivraison(commande.id)}
                          className="btn-action btn-primary"
                          title="Bon de livraison"
                          data-testid={`btn-bon-${commande.id}`}
                        >
                          <Truck size={16} />
                        </button>
                      )}
                      
                      {/* Bouton télécharger facture */}
                      {commande.numero_facture && (
                        <button 
                          onClick={() => genererFacture(commande.id)}
                          className="btn-action btn-success"
                          title={`Facture ${commande.numero_facture}`}
                          data-testid={`btn-facture-${commande.id}`}
                        >
                          <FileText size={16} />
                        </button>
                      )}
                      
                      {/* Bouton télécharger avoir */}
                      {commande.numero_avoir && (
                        <button 
                          onClick={() => genererAvoir(commande.id)}
                          className="btn-action btn-danger"
                          title={`Avoir ${commande.numero_avoir}`}
                          data-testid={`btn-avoir-${commande.id}`}
                        >
                          <FileX2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
